import React, { useEffect, useState } from "react";
import { Button, Descriptions, Drawer, Empty, Segmented, Space, Spin, Table, Timeline, Typography, message } from "antd";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_EDI_LOG_COLLECTION, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { useAuth } from "../../../data/hooks/firebase/useAuth";
import roles from "../../../roles";
import { describeEdifact } from "../../../functions/edifact";
import { EdiMessageTypeTag, EdiStatusTag } from "./EdiLogTags";

const { Text, Title } = Typography;

const preStyle = {
  margin: 0,
  padding: 12,
  maxHeight: 420,
  overflow: "auto",
  background: "#fafafa",
  border: "1px solid #e8e8e8",
  borderRadius: 6,
  fontSize: 12,
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
};

const copy = (text, t) =>
  navigator.clipboard
    .writeText(text)
    .then(() => message.success(t("edi_log_copied")))
    .catch(() => message.error(t("edi_log_copy_failed")));

/** Alle logregels van dezelfde EDI-order, nieuwste eerst. */
export const useOrderHistory = (edifactOrderNumber) => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!edifactOrderNumber) {
      setHistory(null);
      return;
    }
    // Alleen een gelijkheidsfilter: geen composite index nodig, sorteren gebeurt hier.
    const historyQuery = query(
      collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_EDI_LOG_COLLECTION),
      where("edifactOrderNumber", "==", edifactOrderNumber),
    );
    return onSnapshot(
      historyQuery,
      (snapshot) =>
        setHistory(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).sort((a, b) => b.createdAt - a.createdAt),
        ),
      (error) => {
        console.error("Error fetching EDI order history:", error);
        setHistory([]);
      },
    );
  }, [edifactOrderNumber]);

  return history;
};

/** Leesbare segmenten van de API; null tijdens laden, [] als het ophalen mislukt. */
const useDescribedSegments = (rawMessage) => {
  const { t } = useTranslation();
  const [segments, setSegments] = useState(null);

  useEffect(() => {
    if (!rawMessage) return;
    let cancelled = false;
    setSegments(null);
    describeEdifact(rawMessage)
      .then((result) => !cancelled && setSegments(result))
      .catch((error) => {
        console.error("Error describing EDI message:", error);
        if (!cancelled) {
          message.error(t("edi_log_describe_failed"));
          setSegments([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rawMessage, t]);

  return segments;
};

const EdiLogDrawer = ({ entry, open, onClose, onSelect, isMobile }) => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [view, setView] = useState("readable");
  const history = useOrderHistory(entry?.edifactOrderNumber);
  const segments = useDescribedSegments(view === "readable" ? entry?.rawMessage : null);

  if (!entry) return null;

  const createdAt = dayjs.unix(entry.createdAt);
  const isEdifact = entry.rawMessage?.trimStart().startsWith("UN");
  const showReadable = view === "readable" && isEdifact;
  const downloadName = `${entry.messageType}-${entry.edifactOrderNumber || entry.id}-${createdAt.format("YYYYMMDD-HHmmss")}.txt`;

  return (
    <Drawer
      title={`${entry.messageType} · ${t(`edi_log_event_${entry.event}`)} | ${createdAt.format("D MMM YYYY HH:mm:ss")}`}
      placement="right"
      onClose={onClose}
      open={open}
      width={isMobile ? window.innerWidth : 850}
      styles={{ body: { padding: isMobile ? 2 : "16px 16px 50px 16px" } }}
    >
      <Descriptions
        bordered
        title={
          <div className="flex-row">
            {t("edi_log_details")} <EdiStatusTag status={entry.status} />
          </div>
        }
        size="small"
        column={1}
      >
        <Descriptions.Item label={t("id")}>
          {profile?.userRole === roles.superAdmin ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const win = window.open("", "_blank");
                win.document.write(`<pre>${JSON.stringify(entry, null, 2).replace(/</g, "&lt;")}</pre>`);
                win.document.close();
              }}
              style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
            >
              {entry.id}
            </a>
          ) : (
            entry.id
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("edi_log_time")}>
          {createdAt.format("D MMM YYYY HH:mm:ss")} <Text type="secondary">({createdAt.fromNow()})</Text>
        </Descriptions.Item>
        <Descriptions.Item label={t("edi_log_message_type")}>
          <EdiMessageTypeTag entry={entry} /> {t(`edi_log_direction_${entry.direction}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t("edi_log_event")}>{t(`edi_log_event_${entry.event}`)}</Descriptions.Item>
        <Descriptions.Item label={t("edi_log_source")}>{t(`edi_log_source_${entry.source}`)}</Descriptions.Item>
        <Descriptions.Item label={t("edi_log_order_number")}>
          {entry.edifactOrderNumber ? <Text copyable>{entry.edifactOrderNumber}</Text> : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("edi_log_farmorder")}>
          {entry.farmorderId ? <Text copyable>{entry.farmorderId}</Text> : "-"}
        </Descriptions.Item>
        {entry.data?.tid && (
          <Descriptions.Item label={t("edi_log_descartes_tid")}>
            <Text copyable>{entry.data.tid}</Text>
          </Descriptions.Item>
        )}
        {entry.error && (
          <Descriptions.Item label={t("edi_log_error")}>
            <Text type="danger">{entry.error}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      {entry.rawMessage && (
        <div style={{ marginTop: 24 }}>
          <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }} wrap>
            <Title level={5} style={{ margin: 0 }}>
              {t("edi_log_raw_message")}
            </Title>
            <Space wrap>
              {isEdifact && (
                <Segmented
                  size="small"
                  value={view}
                  onChange={setView}
                  options={[
                    { label: t("edi_log_view_readable"), value: "readable" },
                    { label: t("edi_log_view_raw"), value: "raw" },
                  ]}
                />
              )}
              <Button size="small" icon={<CopyOutlined />} onClick={() => copy(entry.rawMessage, t)}>
                {t("edi_log_copy")}
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => saveAs(new Blob([entry.rawMessage], { type: "text/plain" }), downloadName)}
              >
                {t("download")}
              </Button>
            </Space>
          </Space>
          {showReadable ? (
            <Table
              size="small"
              rowKey={(_, index) => index}
              pagination={false}
              scroll={{ y: 420 }}
              loading={!segments}
              dataSource={segments || []}
              columns={[
                {
                  title: t("edi_log_segment"),
                  dataIndex: "segment",
                  width: "45%",
                  render: (segment) => <code style={{ fontSize: 12, wordBreak: "break-all" }}>{segment}</code>,
                },
                {
                  title: t("edi_log_meaning"),
                  dataIndex: "description",
                  render: (description) => description || <Text type="secondary">-</Text>,
                },
              ]}
            />
          ) : (
            <pre style={preStyle}>{entry.rawMessage}</pre>
          )}
        </div>
      )}

      {entry.data && Object.keys(entry.data).length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Title level={5} style={{ marginBottom: 8 }}>
            {t("edi_log_data")}
          </Title>
          <pre style={preStyle}>{JSON.stringify(entry.data, null, 2)}</pre>
        </div>
      )}

      {entry.edifactOrderNumber && (
        <div style={{ marginTop: 24 }}>
          <Title level={5} style={{ marginBottom: 16 }}>
            {t("edi_log_order_history", { orderNumber: entry.edifactOrderNumber })}
          </Title>
          {!history ? (
            <Spin />
          ) : history.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("edi_log_no_entries")} />
          ) : (
            <Timeline
              items={history.map((item) => {
                const isCurrent = item.id === entry.id;
                return {
                  key: item.id,
                  color: item.status === "error" ? "red" : "green",
                  children: (
                    <div
                      onClick={() => !isCurrent && onSelect(item)}
                      style={{ cursor: isCurrent ? "default" : "pointer" }}
                    >
                      <Space size={4} wrap>
                        <Text strong={isCurrent}>{dayjs.unix(item.createdAt).format("DD-MM-YYYY HH:mm:ss")}</Text>
                        <EdiMessageTypeTag entry={item} />
                        <Text strong={isCurrent}>{t(`edi_log_event_${item.event}`)}</Text>
                        <Text type="secondary">· {t(`edi_log_source_${item.source}`)}</Text>
                        {isCurrent && <Text type="secondary">({t("edi_log_current")})</Text>}
                      </Space>
                      {item.error && (
                        <div>
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {item.error}
                          </Text>
                        </div>
                      )}
                    </div>
                  ),
                };
              })}
            />
          )}
        </div>
      )}
    </Drawer>
  );
};

export default EdiLogDrawer;
